import {
  PRODUCT_VISUAL_KINDS,
  type ProductVisualKind,
} from "@nova-ai/product-visual-templates/kinds"

import { SegmentedControl } from "../ui/segmented-control"

// Display copy per visual kind. A `Record` over the kind
// union means adding a kind fails the typecheck until it gets a label.
const PRODUCT_VISUAL_KIND_LABEL: Record<ProductVisualKind, string> = {
  nutrition: "Nutrition",
  "nutrition-multilingual": "Nutrition multilingual",
  amino: "Amino",
  "amino-multilingual": "Amino multilingual",
  etiquette: "Etiquette",
  // Two words, not the hyphenated token: the token is a URL/DB value, this is copy.
  "marketing-ingredients": "Marketing ingredients",
}

export function productVisualKindLabel(kind: ProductVisualKind): string {
  return PRODUCT_VISUAL_KIND_LABEL[kind]
}

// The one visual-kind picker across the app (Studio's images grid, the product
// page's gallery). Options come straight from the template registry, so a new
// kind appears here without touching the UI.
export function ProductVisualKindControl({
  value,
  onChange,
}: {
  value: ProductVisualKind
  onChange: (kind: ProductVisualKind) => void
}) {
  return (
    <SegmentedControl
      label="Visual kind"
      value={value}
      options={PRODUCT_VISUAL_KINDS.map((kind) => ({
        value: kind,
        label: productVisualKindLabel(kind),
      }))}
      onChange={onChange}
    />
  )
}

/** "Every kind" — the product gallery and the template harness both show all
 *  six kinds at once by default and narrow to one; the registry owns no such
 *  member, so it lives here beside the picker that offers it. It is URL-visible
 *  (`/products/:sku?kind=`), which is why it stays a plain `all`. */
export const PRODUCT_VISUAL_KIND_ALL = "all"

export type ProductVisualKindFilter = ProductVisualKind | typeof PRODUCT_VISUAL_KIND_ALL

/** The same picker with a leading All option, for surfaces that render every
 *  kind at once. */
export function ProductVisualKindFilterControl({
  value,
  onChange,
}: {
  value: ProductVisualKindFilter
  onChange: (kind: ProductVisualKindFilter) => void
}) {
  return (
    <SegmentedControl
      label="Visual kind"
      value={value}
      options={[
        { value: PRODUCT_VISUAL_KIND_ALL, label: "All" },
        ...PRODUCT_VISUAL_KINDS.map((kind) => ({
          value: kind,
          label: productVisualKindLabel(kind),
        })),
      ]}
      onChange={onChange}
    />
  )
}
